import styled from "styled-components";
import { breakpoint } from "@utilities";
import { ICustomerInfo, IFareCart, IUpdateItem } from "@interfaces";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { TotalPriceComponent } from "@components";
import { CheckoutItemModule } from "./CheckoutItemModule";
import { fetchRedeemer, useAppDispatch } from "@redux";
import { groupCartItems } from "src/Utilities/cartHelper";

interface IProps {
    items: IFareCart[];
    customers: ICustomerInfo[];
    handleCustomerItemSave: (items: IUpdateItem[]) => Promise<void>;
}

export const NewCheckoutModule = (props: IProps) => {
    const { items, handleCustomerItemSave } = props;
    const dispatch = useAppDispatch();
    const groupedItems = useMemo(() => groupCartItems(items), [items]);

    const [allTravellerData, setAllTravellerData] = useState<IUpdateItem[]>([]);

    useEffect(() => {
        dispatch(fetchRedeemer());
    }, [dispatch]);

    const quantities = useMemo(() => items.reduce(
        (acc, item) => {
            acc[item.id] = item.booking_quantity ?? 1;
            return acc;
        },
        {} as Record<string | number, number>), [items],
    );

    const isCheckoutDisabled = items.some((item) => {
        const travellerData = allTravellerData.find((data) => data.cartItemId === item.id);
        if (!travellerData || !travellerData.bookingData) return true;
        return travellerData.bookingData.some((b) => !b.redeemers || b.redeemers.length === 0);
    });

    const handleTravellerDataUpdate = useCallback((groupData: IUpdateItem[]) => {
        setAllTravellerData((prev) => {
            const newIds = groupData.map((d) => d.cartItemId);
            const filtered = prev.filter((item) => !newIds.includes(item.cartItemId));
            const merged = [...filtered, ...groupData];
            return _.isEqual(merged, prev) ? prev : merged;
        });
    }, []);

    const updateData = async () => {
        try {
            await handleCustomerItemSave(allTravellerData);
        } catch (error) {
            console.error("Error sending cart data:", error);
        }
    };

    return (
        <Container>
            <div className="cartWrapper">
                {_.entries(groupedItems).map(([groupId, groupItems]) => {
                    return (
                        <CheckoutItemModule
                            key={groupId}
                            items={groupItems}
                            groupId={groupId}
                            quantities={quantities}
                            onTravellerDataChange={handleTravellerDataUpdate}
                        />
                    );
                })}
            </div>
            <div className="totalPriceContainer">
                <TotalPriceComponent
                    btnTitle="Payment"
                    onBtnClick={updateData}
                    page="payment"
                    disableNext={isCheckoutDisabled}
                    items={items}
                />
            </div>
        </Container>
    );
};
const Container = styled.div`
    display: flex;
    font-family: Poppins;
    .cartWrapper {
        padding-right: 16px;
        ${breakpoint.breakOnlyMobile`
            padding-bottom : 100px;
        `}
    }
    ${breakpoint.breakTablet`
        & > .cartWrapper {
            width: ${(p) => p.theme.widths.wide_450};
        }
    `}

    ${breakpoint.breakIpadPro`
        & > .cartWrapper {
            width: ${(p) => p.theme.widths.wide_650};
        }
    `}
    ${breakpoint.breakLarge`
        & > .cartWrapper {
            width: ${(p) => p.theme.widths.wide_825};
        }
    `}

    ${breakpoint.breakOnlyMobile`
        display : block;
        padding: 16px 0px 0px 16px;
    `}

    .totalPriceContainer {
        ${breakpoint.breakLarge`
            width : 300px;
        `}

        ${breakpoint.breakOnlyMobile`
            position: fixed;
            bottom: 0;
            left: 0;
            background-color: white;
            z-index: 5;
            width : 100%;
        `}
    }
`;
